import * as RadixPopover from '@radix-ui/react-popover';
import { memo, type ReactNode, useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { WorkspaceAgentClarifyOption } from '@air/component-agent-clarify';
import { usePortalStore } from '@air/primitive-portal';

import { isWorkspaceAgentPanelHotkeyEvent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentPanelHotkeyEvent';

const ignoreSelectBoardOption = () => undefined;

/** The menu focuses its own search once it mounts. Radix would otherwise focus the panel and scroll the thread. */
const preventChooseBoardAutoFocus = (event: Event) => {
  event.preventDefault();
};

export type WorkspaceAgentGuidedVariantSelectBoardPopoverProps = {
  children: ReactNode;
  index: number;
  isDisabled?: boolean;
  label: string;
  optionId: string;
};

export const WorkspaceAgentGuidedVariantSelectBoardPopover = memo(
  ({ children, index, isDisabled = false, label, optionId }: WorkspaceAgentGuidedVariantSelectBoardPopoverProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const portal = usePortalStore((state) => state.portal);

    const handleOpenChange = useCallback((nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
    }, []);

    const handleSelectBoardHotkey = useCallback((event: KeyboardEvent) => {
      if (!isWorkspaceAgentPanelHotkeyEvent(event)) {
        return;
      }

      event.preventDefault();
      setIsOpen(true);
    }, []);

    // Select board sits in `actionButtons`, so it is not in the clarify list's 1–9 bindings.
    useHotkeys(
      String(index + 1),
      handleSelectBoardHotkey,
      {
        enableOnContentEditable: true,
        enabled: index >= 0 && !isDisabled,
        preventDefault: false,
      },
      [handleSelectBoardHotkey, index, isDisabled],
    );

    if (isDisabled) {
      return (
        <WorkspaceAgentClarifyOption
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION"
          index={index}
          label={label}
          optionId={optionId}
        />
      );
    }

    return (
      <RadixPopover.Root onOpenChange={handleOpenChange} open={isOpen}>
        <RadixPopover.Trigger asChild>
          <WorkspaceAgentClarifyOption
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION"
            index={index}
            label={label}
            onSelect={ignoreSelectBoardOption}
            optionId={optionId}
          />
        </RadixPopover.Trigger>
        <RadixPopover.Portal container={portal ?? undefined}>
          {/* Above the option: the agent pane clips a menu that opens downward. */}
          <RadixPopover.Content
            aria-label="Choose board"
            align="start"
            className="z-portal flex h-[min(32rem,var(--radix-popover-content-available-height))] min-h-0 w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-12 border border-gray-4 bg-gray-1 p-2 shadow-popover outline-none"
            collisionPadding={8}
            data-preserves-selection
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CHOOSE_BOARD_MENU"
            onOpenAutoFocus={preventChooseBoardAutoFocus}
            side="top"
            sideOffset={8}
          >
            {children}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

WorkspaceAgentGuidedVariantSelectBoardPopover.displayName = 'WorkspaceAgentGuidedVariantSelectBoardPopover';
